import {
  ISelectedOption,
  IVariant,
  IVariantOption,
  IVariantSummary,
} from '@shared/types/IBundledProduct';

import { isColorOption, isDefaultTitleOption } from './bundled-product-utils';

export const findMatchingVariantByOptions = (
  selectedOptions: ISelectedOption[],
  variants: IVariantSummary[],
) =>
  variants.find((variant) =>
    variant.values.every((variantOption) =>
      selectedOptions.some((option) => variantOption.value === option.value),
    ),
  );

export const isOptionUnavailable = (
  variants: IVariantSummary[],
  option: ISelectedOption,
  selectedOptions?: ISelectedOption[],
  isIncompleteSelection = false,
) => {
  const relevantSelectedOptions = (selectedOptions ?? []).filter((o) => o.name !== option.name);
  if (isIncompleteSelection && relevantSelectedOptions.length === 0) return false;
  const matchingVariant = findMatchingVariantByOptions(
    [option].concat(relevantSelectedOptions),
    variants,
  );
  return !matchingVariant || !matchingVariant.inStock;
};

export const findFirstAvailableOptionValue = (
  options: IVariantOption[],
  variants: IVariantSummary[],
  selectedOption: ISelectedOption,
): string | undefined => {
  const firstSetOfOptions = options[0]; // e.g. usually "Color"
  return firstSetOfOptions.values.find(
    (value) =>
      !isOptionUnavailable(
        variants,
        {
          name: firstSetOfOptions.name,
          value,
        },
        [selectedOption],
      ),
  );
};

interface IHandleUnavailableOptionSelectionParams {
  options: IVariantOption[];
  variants: IVariantSummary[];
  selectedOptions: ISelectedOption[];
  selectedOption: ISelectedOption;
}
export const handleUnavailableOptionSelection = ({
  options,
  variants,
  selectedOptions,
  selectedOption,
}: IHandleUnavailableOptionSelectionParams): ISelectedOption[] => {
  const existingSelectedOptions = selectedOptions
    .filter((option: ISelectedOption) => option.name !== selectedOption.name)
    .concat(selectedOption);

  const incompleteSelection = existingSelectedOptions.length < options.length;
  if (incompleteSelection) {
    return existingSelectedOptions;
  }

  const isAvailable = findMatchingVariantByOptions(existingSelectedOptions, variants)?.inStock;
  if (isAvailable) {
    return existingSelectedOptions;
  }

  const firstOptionSet = options[0]; // e.g., usually "Colour"
  const secondOptionSet = options[1]; // e.g., usually "Size"
  if (selectedOption.name === firstOptionSet.name) {
    // If user selects an option from the first set (Colour) and it's not available,
    // allow them to select it but reset the second option set (Size)
    return existingSelectedOptions.filter((x) => x.name !== secondOptionSet.name);
  }

  // If user selects an option from the second set (Size) and it's not available,
  // allow them to select it and choose the first available option from the first set (Colour)
  const firstAvailableOption = findFirstAvailableOptionValue(options, variants, selectedOption);
  if (!firstAvailableOption) {
    // if second set option totally out of stock, reset first option set
    return existingSelectedOptions.filter((x) => x.name !== firstOptionSet.name);
  }
  return existingSelectedOptions
    .filter((x: ISelectedOption) => x.name !== firstOptionSet.name)
    .concat([{ name: firstOptionSet.name, value: firstAvailableOption }]);
};

export const setDefaultSelectedOptions = (options: IVariantOption[]): ISelectedOption[] => {
  // Shopify passes the default variant as an option called 'Title' if no variants are present
  const defaultTitleOption = options.find((option) => isDefaultTitleOption(option.name));
  const colorOption = options.find((option) => isColorOption(option.name));

  return [
    colorOption && { name: colorOption.name, value: colorOption.values[0] },
    defaultTitleOption && { name: defaultTitleOption.name, value: defaultTitleOption.values[0] },
  ].filter(Boolean) as ISelectedOption[]; // remove nulls
};

export const getOptionsWithUnavailabilityStatus = (
  options: IVariantOption[],
  variants: IVariantSummary[],
  selectedOptions: ISelectedOption[] = [],
): IVariantOption[] => {
  const isIncompleteSelection =
    selectedOptions.length > 0 && selectedOptions.length < options.length;

  return options.map(({ name, values, ...rest }) => ({
    ...rest,
    name,
    values,
    unavailableValues: values.filter((value) =>
      isOptionUnavailable(variants, { name, value }, selectedOptions, isIncompleteSelection),
    ),
  }));
};

export const toVariantSummary = (
  variants: IVariant[],
  enableAllVariants = false,
): IVariantSummary[] => {
  if (!variants.every((variant) => variant.selectedOptions)) {
    return variants as unknown as IVariantSummary[];
  }
  return variants.map((variant) => ({
    variantId: variant.id,
    values: variant.selectedOptions.map(({ value }) => ({ value })),
    inStock: enableAllVariants || variant.available || variant.availableForSale || false,
  }));
};
